import { useRef } from "react";
import { createPortal } from "react-dom";
import { useModalA11y } from "../hooks/useModalA11y.js";
import { formatUsdcCompact, displayAprLabel } from "../utils/format.js";

/**
 * Confirmation modal for detaching a deposit from a Consolidated
 * Reinvestment Pool. Per V6.2 Final spec §I.6 the only manual user
 * action on a pooled deposit is **detach** — "Lazy settlement is
 * explicitly excluded. Execution operates through keeper-based
 * automation." The earlier "Reinvest Bonus" preview path (compound-
 * minus-simple bonus crystallisation) is not in spec and was removed
 * along with the "Pool Bonus" / "Projected" UI metrics.
 *
 * On detach: the deposit stops contributing to the pool's pending
 * sweep, its accrued interest stays on the deposit (continues to
 * accrue under the standard 20% APR per Bond contract), and the NFT
 * is unlocked for transfer.
 *
 * State shape:
 *   { member: PoolMember, poolId: number }
 *   or null when closed.
 */
export default function SettlementPreviewModal({ state, busy, onClose, onConfirm }) {
    const dialogRef = useRef(null);
    useModalA11y({
        open: Boolean(state),
        onClose: () => !busy && onClose?.(),
        containerRef: dialogRef
    });
    if (!state) return null;

    const { member, poolId } = state;
    const principal = BigInt(member.principal || 0);
    const accruedInterest = BigInt(member.accruedInterest || 0);
    const daysInPool = Number(member.daysCompounded || 0);
    // §V6.2 — display-only APR label for the "keeps growing under X% APR"
    // copy (Turbo shows "up to 27.5%", accrues 20%). Falls back to the real
    // rate when the member row carries no productMode.
    const aprPct = displayAprLabel(member);

    return createPortal(
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4"
            role="presentation"
            onClick={() => !busy && onClose?.()}
        >
            <div
                ref={dialogRef}
                className="w-full max-w-md rounded-[16px] bg-[#10281b] ring-1 ring-[#bfb67b] shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="detach-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="border-b border-[#bfb67b]/30 px-5 py-4">
                    <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                        Confirm Detach
                    </div>
                    <h3
                        id="detach-confirm-title"
                        className="mt-0.5 font-manrope text-[15px] font-bold text-[#e6c985]"
                    >
                        Remove Deposit #{member.depositId} from Pool #{poolId}
                    </h3>
                </div>

                <div className="space-y-3 px-5 py-4 text-white">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-4 py-3">
                            <div className="text-[9.5px] font-semibold uppercase tracking-widest text-[#e6c985]/80">
                                Principal
                            </div>
                            <div className="mt-1 font-manrope text-[18px] font-bold text-white">
                                ${formatUsdcCompact(principal)}{" "}
                                <span className="text-[11px] text-[#e6c985]/70">USDC</span>
                            </div>
                            <div className="mt-0.5 text-[9.5px] text-[#e6c985]/65">
                                Locked at deposit open.
                            </div>
                        </div>
                        <div className="rounded-[10px] bg-white/10 ring-1 ring-[#FBC206] px-4 py-3">
                            <div className="text-[9.5px] font-semibold uppercase tracking-widest text-[#FBC206]">
                                Accrued interest
                            </div>
                            <div className="mt-1 font-manrope text-[18px] font-bold text-white">
                                ${formatUsdcCompact(accruedInterest)}{" "}
                                <span className="text-[11px] text-[#e6c985]/70">USDC</span>
                            </div>
                            <div className="mt-0.5 text-[9.5px] text-[#e6c985]/65">
                                After {daysInPool} day{daysInPool === 1 ? "" : "s"} in pool.
                            </div>
                        </div>
                    </div>

                    <div className="rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 px-3 py-2.5 text-[11px] leading-snug text-white/85">
                        <span className="font-semibold text-[#FBC206]">After this tx:</span>{" "}
                        Deposit #{member.depositId} stops contributing to Pool #{poolId}.
                        Its accrued interest stays on the deposit and keeps growing under
                        its {aprPct}% APR. The NFT is unlocked for transfer.
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-[#bfb67b]/30 px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="rounded-full bg-white/10 px-4 py-1.5 text-[12px] font-semibold text-white ring-1 ring-[#bfb67b] hover:bg-white/20 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#FBC206] px-5 py-1.5 text-[12px] font-bold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0"
                    >
                        {busy ? "Submitting…" : "Detach"}
                        <svg
                            className="h-3 w-3"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <polyline points="7 4 13 10 7 16" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
